/**
 * The little of IndexedDB this app uses, as promises.
 *
 * IndexedDB, not `localStorage`, because the copy of a person's work kept for
 * offline use does not fit in `localStorage`'s few megabytes (.docs/023). A
 * wrapper of our own rather than a library: four calls are all that is needed,
 * and every one of them is easy to read here.
 */

function done<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Storage failed'));
  });
}

/**
 * Opens a database, creating or upgrading it first. `stores` names every
 * object store it must have; a store is only ever added, never removed, so an
 * older copy upgrades without losing what it holds.
 */
export function openDb(name: string, version: number, stores: readonly string[]): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, version);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const store of stores) {
        if (!db.objectStoreNames.contains(store)) db.createObjectStore(store);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(`Could not open ${name}`));
    // Another tab holds an older version open; it is asked to close (see
    // `onversionchange` below) and this waits for it.
    request.onblocked = () => undefined;
  }).then((db) => {
    // A newer tab wants to upgrade: let it, rather than both tabs waiting.
    (db as IDBDatabase).onversionchange = () => (db as IDBDatabase).close();
    return db as IDBDatabase;
  });
}

export function getValue<T>(db: IDBDatabase, store: string, key: IDBValidKey): Promise<T | undefined> {
  return done(db.transaction(store, 'readonly').objectStore(store).get(key)) as Promise<T | undefined>;
}

export async function putValue(db: IDBDatabase, store: string, key: IDBValidKey, value: unknown): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).put(value, key);
  await committed(tx);
}

export async function deleteValue(db: IDBDatabase, store: string, key: IDBValidKey): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(key);
  await committed(tx);
}

/** Every key in a store, for listing who has data here. */
export function allKeys(db: IDBDatabase, store: string): Promise<IDBValidKey[]> {
  return done(db.transaction(store, 'readonly').objectStore(store).getAllKeys());
}

export function deleteDb(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error(`Could not delete ${name}`));
    // Another tab still has it open; it is deleted once that tab lets go.
    request.onblocked = () => resolve();
  });
}

function committed(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Storage failed'));
    tx.onabort = () => reject(tx.error ?? new Error('Storage was stopped'));
  });
}
